/**
 * FILTRE ER CHIPS (Glass 2, DESIGN.md §16.5 punkt 4).
 *
 * Tonal kapsel når filteret er av, AKSENTFYLL og aksenttekst når det er på — «Bare det som mangler
 * folk», «Uten pris», «Byrå · 3». Et påslått filter må se påslått ut hele tiden: et glemt filter er
 * en liste med fire rader og ingen forklaring på skjermen.
 *
 * Klassene står her, én gang, fordi fire kontroller bærer dem: denne av/på-chipen, `Select`s
 * `pill`, `FilterMultiSelect` og prischipen på Booking. En kontroll som ser annerledes ut enn naboen
 * på samme linje leses som noe annet.
 *
 * Aldri primærknappen for et filter. Den er «maks én per skjerm», og den er en handling.
 */
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { cn } from '@/lib/cn';

import { ICONS } from './icons';

/** Formen, som er den samme av og på. 28 px, som en `sm`-knapp; `hit-target` gir 44 å treffe. */
const CHIP_SHAPE = cn(
  'ring-focus hit-target inline-flex h-7 max-w-full shrink-0 items-center gap-1.5',
  'rounded-[var(--radius-control)] border border-transparent px-3 text-xs whitespace-nowrap',
  'transition-[background-color,color,filter] duration-[var(--duration-fast)]',
  'disabled:pointer-events-none disabled:opacity-50',
);

/**
 * Chipens klasser. `on` er om filteret er i bruk — ikke om menyen er åpen.
 *
 * Av: tonal (`control-tonal`, som også følger `controls-on-backdrop`), tekst i full styrke — et
 * filter er en handling og skal ikke se ut som metadata (G6). På: aksentfyll og aksenttekst, 600.
 */
export function filterChipClass(on: boolean): string {
  return cn(
    CHIP_SHAPE,
    on
      ? 'bg-accent-subtle font-semibold text-accent-text hover:brightness-[.97]'
      : 'control-tonal font-medium text-default',
  );
}

/** `▾` — husets egen pil, ikke et tegn i en streng. Dempet av, i chipens egen farge på. */
export function ChipCaret({ on }: { on: boolean }): ReactNode {
  const Chevron = ICONS.chevronDown;
  return <Chevron aria-hidden className={cn('size-3.5 shrink-0', on ? null : 'text-muted')} />;
}

export interface FilterChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Filteret er på. Blir `aria-pressed`, så tilstanden ikke bæres av farge alene (§3). */
  active: boolean;
  onToggle: () => void;
  ref?: Ref<HTMLButtonElement>;
  children: ReactNode;
}

/** Av/på-chipen: «Bare det som mangler folk», «Uten pris», «Trenger avklaring». */
export function FilterChip({
  active,
  onToggle,
  className,
  children,
  type = 'button',
  ...rest
}: FilterChipProps): ReactNode {
  return (
    <button
      type={type}
      aria-pressed={active}
      onClick={onToggle}
      className={cn(filterChipClass(active), className)}
      {...rest}
    >
      {children}
    </button>
  );
}
